'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import type { Currency } from 'utils/Currency';
import type { InstallmentConfig } from 'utils/installmentConfig';
import type { CardConfig, ResolveCtx, SelectionsMap } from './cardSchema';

export type SplitPaymentsDisplay = {
  icons: string[];
  text: ReactNode;
};

export type OfferRow = {
  image?: string[];
  title: ReactNode;
};

export type BuyContextValue = {
  cards: CardConfig[];
  store: ShopifyStore;
  currency: Currency;
  productName: string;
  basePrice: number;
  baseVariantId?: string;

  installmentConfig?: InstallmentConfig | null;
  splitPaymentsDisplay?: SplitPaymentsDisplay | null;
  offersData?: OfferRow[] | null;
  dispatchDate?: string | null;
  onSeeAllPaymentOptions?: () => void;

  selections: SelectionsMap;
  setSelection: (groupId: string, value: string | null | string[]) => void;

  isCheckoutLoading: boolean;
  setCheckoutLoading: (loading: boolean) => void;

  resolveCtx: ResolveCtx;
};

const noop = () => undefined;

const initialContext: BuyContextValue = {
  cards: [],
  store: 'ROW' as ShopifyStore,
  currency: 'usd' as Currency,
  productName: '',
  basePrice: 0,
  baseVariantId: undefined,
  installmentConfig: null,
  splitPaymentsDisplay: null,
  offersData: null,
  dispatchDate: null,
  onSeeAllPaymentOptions: undefined,
  selections: {},
  setSelection: noop,
  isCheckoutLoading: false,
  setCheckoutLoading: noop,
  resolveCtx: {
    store: 'ROW' as ShopifyStore,
    currency: 'usd' as Currency,
    selections: {},
  },
};

export const BuyContext = createContext<BuyContextValue>(initialContext);

export const useBuyContext = (): BuyContextValue => useContext(BuyContext);

export type BuyProviderProps = {
  children: ReactNode;
  cards: CardConfig[];
  store: ShopifyStore;
  currency: Currency;
  productName: string;
  basePrice: number;
  baseVariantId?: string;
  installmentConfig?: InstallmentConfig | null;
  splitPaymentsDisplay?: SplitPaymentsDisplay | null;
  offersData?: OfferRow[] | null;
  dispatchDate?: string | null;
  onSeeAllPaymentOptions?: () => void;
  initialSelections?: SelectionsMap;
};

export const BuyProvider = ({
  children,
  cards,
  store,
  currency,
  productName,
  basePrice,
  baseVariantId,
  installmentConfig,
  splitPaymentsDisplay,
  offersData,
  dispatchDate,
  onSeeAllPaymentOptions,
  initialSelections,
}: BuyProviderProps) => {
  const [selections, setSelections] = useState<SelectionsMap>(
    initialSelections ?? {},
  );
  const [isCheckoutLoading, setCheckoutLoading] = useState(false);

  const setSelection = useCallback(
    (groupId: string, value: string | null | string[]) => {
      setSelections((prev) => ({ ...prev, [groupId]: value }));
    },
    [],
  );

  const resolveCtx = useMemo<ResolveCtx>(
    () => ({ store, currency, selections }),
    [store, currency, selections],
  );

  const value = useMemo<BuyContextValue>(
    () => ({
      cards,
      store,
      currency,
      productName,
      basePrice,
      baseVariantId,
      installmentConfig,
      splitPaymentsDisplay,
      offersData,
      dispatchDate,
      onSeeAllPaymentOptions,
      selections,
      setSelection,
      isCheckoutLoading,
      setCheckoutLoading,
      resolveCtx,
    }),
    [
      cards,
      store,
      currency,
      productName,
      basePrice,
      baseVariantId,
      installmentConfig,
      splitPaymentsDisplay,
      offersData,
      dispatchDate,
      onSeeAllPaymentOptions,
      selections,
      setSelection,
      isCheckoutLoading,
      resolveCtx,
    ],
  );

  return <BuyContext.Provider value={value}>{children}</BuyContext.Provider>;
};
